import React, { useState, useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Box, Typography, TextField, Button, Grid } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import {
  Paper, IconButton, Stack, MenuItem, InputLabel, FormControl,
  Select, FormHelperText, Tooltip, Pagination
} from '@mui/material';
import {
  DataGrid,
} from '@mui/x-data-grid';
import { Edit, DeleteOutline, QrCode2, DownloadOutlined } from '@mui/icons-material';
import RightDrawer from '../components/RightDrawer'; // Adjust path if needed
import { Chip } from '@mui/material';
import { SAVE, GET_LISTBYID, DELETE_DATA, UPDATE, GET_LIST, GET_FILE } from '../utils/api';
import { CircularProgress } from '@mui/material';
import DeleteConfirmationDialog from '../components/DeleteConfirmationDialog';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { Alert, Snackbar } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const getStatusChip = (status) => {
  const normalized = status?.toLowerCase();

  switch (normalized) {
    case 'in-stock':
      return (
        <Chip
          label="In-Stock"
          size="small"
          sx={{
            color: '#2e7d32',
            backgroundColor: '#e8f5e9',
            border: '1px solid #66BB6A',
            fontWeight: 'bold',
          }}
        />
      );
    case 'in-service':
      return (
        <Chip
          label="In-Service"
          size="small"
          sx={{
            color: '#d84315',
            backgroundColor: '#fbe9e7',
            border: '1px solid #FF6E40',
            fontWeight: 'bold',
          }}
        />
      );
    case 'in-operation':
      return (
        <Chip
          label="In-Operation"
          size="small"
          sx={{
            color: '#1565c0',
            backgroundColor: '#e3f2fd',
            border: '1px solid #42A5F5',
            fontWeight: 'bold',
          }}
        />
      );
    case 'active':
      return (
        <Chip
          label="Active"
          size="small"
          sx={{
            color: '#6a1b9a',
            backgroundColor: '#f3e5f5',
            border: '1px solid #AB47BC',
            fontWeight: 'bold',
          }}
        />
      );
    default:
      return (
        <Chip
          label={status}
          size="small"
          sx={{
            color: 'gray',
            backgroundColor: '#f0f0f0',
            fontWeight: 'bold',
          }}
        />
      );
  }
};

const Devices = () => {
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState('id');
  const [order, setOrder] = useState('asc');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const formRef = useRef();
  const [deviceName, setDeviceName] = useState('');
  const [deviceID, setDeviceID] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [status, setStatus] = useState('');
  const [deviceCategory, setDeviceCategory] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [qrImage, setQrImage] = useState('');
  const [errors, setErrors] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [alert, setAlert] = useState({ type: '', message: '', open: false });
  const [editId, setEditId] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [totalPages, setTotalPages] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Functions -> 

  // functions for opening and closing drawer
  const handleOpenDrawer = () => setDrawerOpen(true);
  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setErrors({});
    setEditId(null);
    setDeviceName('');
    setDeviceID('');
    setSerialNumber('');
    setStatus('');
    setDeviceCategory('');
  };


  //To validate the fields
  const validateInventoryForm = () => {
    const newErrors = {};
    if (!deviceName) newErrors.deviceName = 'Device number is required';
    if (!deviceID) newErrors.deviceID = 'Device ID is required';
    if (!serialNumber) newErrors.serialNumber = 'Serial number is required';
    if (!status) newErrors.status = 'Status is required';
    if (!deviceCategory) newErrors.deviceCategory = 'Device category is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // To save/ edit the inventory
  const handleSaveInventory = async () => {
    if (!validateInventoryForm()) return;

    const payload = {
      device_number: Number(deviceName),
      device_id: deviceID,
      serial_number: serialNumber,
      upload_image: "https://images.unsplash.com/photo-1581291518857-4e27b48ff24e",
      device_category: deviceCategory,
      status: status,
      ports_number: deviceCategory == "4 PortHub" ? 4 : deviceCategory == "6 PortHub" ? 6 : 8
    };

    try {
      let response;
      if (editId) {
        // Edit
        response = await UPDATE(`/device-inventory/${editId}`, payload);
      } else {
        // Create
        response = await SAVE('/device-inventory', payload);
      }

      if (response.statusCode == 401) {
        logout();
        navigate('/login');
      }
      if (response.status === 200 || response.status === 201) {
        setAlert({
          type: 'success',
          message: editId ? 'Inventory updated successfully!' : 'Inventory saved successfully!',
          open: true,
        });
        handleCloseDrawer();
        await fetchInventory();
      } else {
        setAlert({
          type: 'error',
          message: response?.data.message || `Failed with status ${response?.data.message}`,
          open: true,
        });
      }
    } catch (error) {
      setAlert({
        type: 'error',
        message: error?.message || 'Something went wrong.',
        open: true,
      });
      console.error('Error saving inventory:', error);
    }
  };

  // Function for when we will click edit button
  const handleEditClick = async (id) => {
    try {
      const response = await GET_LISTBYID('/device-inventory', id);
      const data = response;

      setEditId(data._id); // Set current editing ID
      setDeviceName(data.device_number?.toString() || '');
      setDeviceID(data.device_id || '');
      setSerialNumber(data.serial_number || '');
      setDeviceCategory(data.device_category || '');
      setStatus(data.status || '');

      setDrawerOpen(true);
    } catch (error) {
      console.error('Failed to fetch item for edit:', error);
      setAlert({
        type: 'error',
        message: 'Failed to load item for editing.',
        open: true,
      });
    }
  };


  // To fetch the devices list 
  const fetchInventory = async (searchText = '') => {
    setLoading(true);
    try {
      // Construct query parameters as an object
      let queryParams = {
        ...(searchText ? { searchText } : {}),
        limit: limit
      };

      const response = await GET_LIST('/device-inventory', queryParams);
      if (response.statusCode == 401) {
        logout();
        navigate('/login');
      }
      setRows(response?.data || []);
      setTotalPages(response.data.totalPages);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch inventory:', err);
      setError('Error loading inventory');
    } finally {
      setLoading(false);
    }
  };

  // download function 
  const handleDownload = async () => {
    try {
      const queryParams = {
        ids: selected.join(','),
      };
      await GET_FILE('/device-inventory/download/excel', queryParams, 'xlsx', 'device-inventory');
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  const handleOpenDeleteDialog = (id) => {
    setSelectedId(id);
    setDeleteDialogOpen(true);
  };

  // Close dialog
  const handleCloseDeleteDialog = () => {
    setSelectedId(null);
    setDeleteDialogOpen(false);
  };

  // Confirm delete
  const handleConfirmDelete = async () => {
    try {
      await DELETE_DATA('/device-inventory', selectedId);
      setAlert({
        type: 'success',
        message: 'Record deleted successfully.',
        open: true,
      });
      await fetchInventory(searchText);
    } catch (error) {
      setAlert({
        type: 'error',
        message: 'Failed to delete the record.',
        open: true,
      });
    } finally {
      handleCloseDeleteDialog();
    }
  };


  useEffect(() => {
    fetchInventory(searchText);
  }, []);
  const columns = [
    {
      field: 'device_id',
      headerName: 'Device ID',
      sortable: true,
      flex: 1,
    },
    {
      field: 'serial_number',
      headerName: 'Serial No',
      sortable: false,
      flex: 1,
    },
    {
      field: 'device_category',
      headerName: 'Device Category',
      sortable: false,
      flex: 1,
    },
    {
      field: 'status',
      headerName: 'Status',
      sortable: false,
      flex: 1,
      renderCell: (params) => getStatusChip(params.value),
    },
    {
      field: 'actions',
      headerName: 'Action',
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => handleEditClick(params.id)}
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Delete">
            <IconButton
              size="small"
              sx={{ color: 'red' }}
              onClick={() => handleOpenDeleteDialog(params.id)}
            >
              <DeleteOutline fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Show QR Code">
            <IconButton
              size="small"
              onClick={() => {
                setQrImage(params.row.qrCode);
                setQrModalOpen(true);
              }}
            >
              <QrCode2 fontSize="small" />
            </IconButton>
          </Tooltip>
        </>
      ),
    },
  ];
  return (
    <Box>
      <Typography variant="h6" sx={{ color: 'black', fontWeight: 'bold' }}>
        Device Inventory
      </Typography>
      <Typography variant="body2" sx={{ color: 'gray', marginBottom: 1 }}>
        Last Updated: {new Date().toLocaleDateString()}
      </Typography>

      {/* Row containing search, upload, and add inventory */}
      <Grid
        container
        spacing={0.5}
        sx={{ mb: 1 }}
        alignItems="center"
        justifyContent="flex-end"
        wrap="nowrap"
      >
        {/* Search Field */}
        <Grid item>
          <TextField
            id="outlined-size-small"
            label="Search"
            variant="outlined"
            size="small"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setPage(1)
                fetchInventory(searchText);
              }
            }}
            fullWidth
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: 'gray', mr: 1 }} />,
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
              },
            }}
          />
        </Grid>

        {selected.length > 0 && (
          <Grid item>
            <Button variant="contained" startIcon={<DownloadOutlined />} size="small" sx={{ p: 0.5 }} color="primary" onClick={handleDownload}>
              Download
            </Button>
          </Grid>
        )}

        {/* Add Inventory Button */}
        {
          user?.role === 'admin' && (
            <Grid item>
              <Button
                onClick={handleOpenDrawer}
                startIcon={<Add />}
                size="small"
                variant='contained'
              >
                Add Inventory
              </Button>
            </Grid>
          )
        }
      </Grid>

      <Paper sx={{ width: '100%', mb: 2, borderRadius: 2, overflow: 'hidden' }}>
        {loading ? (
          <Box
            sx={{
              height: 'calc(100vh - 190px)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <CircularProgress size={30} />
          </Box>
        ) : (
          <>
            <Box sx={{ height: 'calc(100vh - 210px)', width: '100%' }}>
              <DataGrid
                rows={rows}
                columns={columns}
                checkboxSelection
                disableRowSelectionOnClick
                hideFooterPagination
                sortingOrder={['asc', 'desc']}
                getRowClassName={(params) =>
                  params.indexRelativeToCurrentPage % 2 === 0 ? 'even-row' : 'odd-row'
                }
                onRowSelectionModelChange={(selectionModel) => {
                  const idsArray = Array.from(selectionModel.ids || []);
                  setSelected(idsArray);
                }}
                sx={{
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: '#f2f2f2',
                  },
                  '& .even-row': {
                    backgroundColor: '#f6f6f6',
                  },
                  '& .odd-row': {
                    backgroundColor: '#ffffff',
                  },
                  '& .MuiDataGrid-footerContainer': {
                    minHeight: '0px'
                  },
                  border: 'none',
                }}
              />
            </Box>
            <div style={{ display: "flex", justifyContent: "flex-end", padding: "6px" }}>
              <Pagination
                count={totalPages}
                page={page}
                color="primary"
                onChange={(e, value) => setPage(value)}
                shape="rounded"
              />
            </div>
          </>
        )}

      </Paper>
      <RightDrawer
        open={drawerOpen}
        onClose={handleCloseDrawer}
        onSave={handleSaveInventory}
        title={editId ? "Edit Inventory" : "Add New Inventory"}
      >
        <Stack spacing={2} sx={{ width: '100%' }}>
          <TextField
            required
            size="small"
            fullWidth
            label="Device Number"
            placeholder="Enter device number"
            value={deviceName}
            onChange={(e) => setDeviceName(e.target.value)}
            error={!!errors.deviceName}
            helperText={errors.deviceName}
          />

          <TextField
            required
            size="small"
            fullWidth
            label="Device ID"
            placeholder="Enter device ID"
            value={deviceID}
            onChange={(e) => setDeviceID(e.target.value)}
            error={!!errors.deviceID}
            helperText={errors.deviceID}
          />

          <TextField
            required
            size="small"
            fullWidth
            label="Serial Number"
            placeholder="Enter serial number"
            value={serialNumber}
            onChange={(e) => setSerialNumber(e.target.value)}
            error={!!errors.serialNumber}
            helperText={errors.serialNumber}
          />

          <FormControl
            required
            fullWidth
            size="small"
            error={!!errors.status}
          >
            <InputLabel>Status</InputLabel>
            <Select
              value={status}
              label="Status"
              onChange={(e) => setStatus(e.target.value)}
            >
              {/* <MenuItem value="In-Operation">In-Operation</MenuItem> */}
              <MenuItem value="In-Stock">In-Stock</MenuItem>
              {/* <MenuItem value="In-Service">In-Service</MenuItem> */}
            </Select>
            {errors.status && <FormHelperText>{errors.status}</FormHelperText>}
          </FormControl>

          <FormControl
            required
            fullWidth
            size="small"
            error={!!errors.deviceCategory}
          >
            <InputLabel>Device Category</InputLabel>
            <Select
              value={deviceCategory}
              label="Device Category"
              onChange={(e) => setDeviceCategory(e.target.value)}
            >
              <MenuItem value="4 PortHub">4 PortHub</MenuItem>
              <MenuItem value="6 PortHub">6 PortHub</MenuItem>
              <MenuItem value="8 PortHub">8 PortHub</MenuItem>
            </Select>
            {errors.deviceCategory && <FormHelperText>{errors.deviceCategory}</FormHelperText>}
          </FormControl>

        </Stack>

      </RightDrawer>

      <Dialog open={qrModalOpen} onClose={() => setQrModalOpen(false)}>
        <DialogTitle
          sx={{ bgcolor: '#1e2a38', color: 'white', px: 1 }}
        >
          QR Code
        </DialogTitle>

        <DialogContent sx={{ textAlign: 'center', p: 1, mt: 3 }}>
          {qrImage && (
            <img
              src={`data:image/png;base64,${qrImage}`}
              alt="QR Code"
              style={{ width: '200px', height: '200px', marginBottom: '16px' }}
            />
          )}
        </DialogContent>

        <DialogActions sx={{ bgcolor: '#e3eaf2', justifyContent: 'end', px: 3 }}>
          <Button onClick={() => setQrModalOpen(false)}>Close</Button>
          <Button
            variant="outlined"
            onClick={() => {
              const printWindow = window.open('', '_blank');
              printWindow.document.write(`
          <html>
            <head><title>Print QR</title></head>
            <body style="display:flex;justify-content:center;align-items:center;height:100vh;">
              <img src="data:image/png;base64,${qrImage}" style="width:300px;height:300px;" />
              <script>
                window.onload = function() { window.print(); }
              </script>
            </body>
          </html>
        `);
              printWindow.document.close();
            }}
          >
            Print
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={alert.open}
        autoHideDuration={4000}
        onClose={() => setAlert({ ...alert, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={alert.type}
          variant="filled"
          onClose={() => setAlert({ ...alert, open: false })}
          sx={{ width: '100%' }}
        >
          {alert.message}
        </Alert>
      </Snackbar>

      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
        title="Confirm Delete"
        message="Are you sure you want to delete this record?"
      />

    </Box>
  );
};

export default Devices;
